import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import AdvisoryPill from "@/components/AdvisoryPill";
import { Info, PauseCircle, PlayCircle } from "lucide-react";

// Inline advisory row for the Rota page. Returns null when the
// advisory is disabled for this tenant, OR paused for this session.

export function AdvisoryRowRota({ onDate }) {
  const [data, setData] = useState(null);
  const [paused, setPaused] = useState(false);

  const fetchPills = async () => {
    if (paused) return;
    try {
      const { data } = await api.post("/advisory/rota-day",
        { on_date: onDate });
      setData(data);
    } catch { setData({ enabled: false, pills: [] }); }
  };
  useEffect(() => { fetchPills(); /* eslint-disable-next-line */ }, [onDate, paused]);

  if (paused) {
    return (
      <button type="button" onClick={() => setPaused(false)}
        data-testid="rota-advisory-resume"
        className="text-[11px] text-[#4B5773] inline-flex items-center gap-1 hover:text-[#0B1A36]">
        <PlayCircle className="h-3 w-3" />Resume advisory
      </button>
    );
  }
  if (!data || !data.enabled || data.pills.length === 0) return null;
  return (
    <div className="flex items-center gap-2 flex-wrap p-2 rounded-lg bg-[#F6F9FE] border border-[#DBE7FE] mb-3"
         data-testid="rota-advisory-row">
      <span className="text-[11px] text-[#4B5773] inline-flex items-center gap-1">
        <Info className="h-3 w-3" />Advisory
      </span>
      {data.pills.map((p) => (
        <AdvisoryPill key={p.signature} pill={p}
          testid={`rota-advisory-pill-${p.signature}`}
          onDismiss={fetchPills} />
      ))}
      <button type="button" onClick={() => setPaused(true)}
        data-testid="rota-advisory-pause"
        className="ml-auto text-[11px] text-[#4B5773] inline-flex items-center gap-1 hover:text-[#0B1A36]">
        <PauseCircle className="h-3 w-3" />Pause for this session
      </button>
    </div>
  );
}

export function AdvisoryRowLeave({ employeeId, startDate, endDate, leaveType }) {
  const [data, setData] = useState(null);
  const [paused, setPaused] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    if (paused || !employeeId || !startDate || !endDate) return;
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        const { data } = await api.post("/advisory/leave-request", {
          employee_id: employeeId, start_date: startDate,
          end_date: endDate, leave_type: leaveType || "annual",
        });
        setData(data);
      } catch { setData({ enabled: false, pills: [] }); }
    }, 300);
    return () => clearTimeout(timer.current);
  }, [employeeId, startDate, endDate, leaveType, paused]);

  if (paused) {
    return (
      <button type="button" onClick={() => setPaused(false)}
        data-testid="leave-advisory-resume"
        className="text-[11px] text-[#4B5773] inline-flex items-center gap-1">
        <PlayCircle className="h-3 w-3" />Resume advisory
      </button>
    );
  }
  if (!data || !data.enabled || data.pills.length === 0) return null;
  return (
    <div className="flex items-center gap-2 flex-wrap p-2 rounded-lg bg-[#F6F9FE] border border-[#DBE7FE]"
         data-testid="leave-advisory-row">
      <span className="text-[11px] text-[#4B5773] inline-flex items-center gap-1">
        <Info className="h-3 w-3" />Advisory
      </span>
      {data.pills.map((p) => (
        <AdvisoryPill key={p.signature} pill={p}
          testid={`leave-advisory-pill-${p.signature}`} />
      ))}
      <button type="button" onClick={() => setPaused(true)}
        data-testid="leave-advisory-pause"
        className="ml-auto text-[11px] text-[#4B5773] inline-flex items-center gap-1">
        <PauseCircle className="h-3 w-3" />Pause
      </button>
    </div>
  );
}
